import { useState, useRef } from "react";
import {
  Globe, Database, Shield, ChevronDown, ChevronUp,
  Brain, TrendingUp, GraduationCap, Building2, Heart,
  Award, Smartphone, Terminal
} from "lucide-react";
import {
  STATS, DIIA_ECOSYSTEM, NEWS_CITATIONS
} from "../../data/presentationData";
import { useInView } from "./hooks";
import { AnimatedCounter, CitationLink, CitationBar } from "./shared";

export function Section1() {
  const ref = useRef(null);
  const active = useInView(ref, 0.3);
  const [tooltip, setTooltip] = useState(null);
  const [showEcosystem, setShowEcosystem] = useState(false);
  const diiaStats = [
    { value: STATS.diiaUsers, suffix: "+", label: "Diia Users", color: "text-sky-400" },
    { value: STATS.digitalServices, suffix: "+", label: "Digital Services", color: "text-sky-400" },
    { value: STATS.openDatasets, suffix: "+", label: "Open Datasets", color: "text-sky-400" },
  ];
  const ncfStats = [
    { value: STATS.fortuneRank, prefix: "#", label: "Fortune Ranking", color: "text-[#C5A258]" },
    { value: STATS.employment, suffix: "%", label: "Graduate Employment", color: "text-emerald-400" },
    { value: 162, prefix: "$", suffix: "K", label: "USDA Grant (2025)", color: "text-emerald-400" },
  ];
  const diiaTooltips = [
    "23M+ active users \u2014 over 81% of Ukraine's adult population",
    "200+ digital services: ID, driver's license, tax, voting, AI assistant, cashback",
    "80K+ datasets on data.gov.ua \u2014 Ukraine ranks 3rd in EU for open data maturity",
  ];
  const ncfTooltips = [
    "NCF ranked #8 nationally by Fortune/Statista (2025) \u2014 only Florida university listed",
    "100% employment rate for Applied Data Science graduates",
    "Three consecutive years of USDA partnership \u2014 $98K to $130K to $162K growth",
  ];
  const ecosystemIcons = { smartphone: Smartphone, brain: Brain, database: Database, shield: Shield, trending: TrendingUp, building: Building2, heart: Heart, globe: Globe, graduation: GraduationCap, briefcase: Award, code: Terminal };
  return (
    <div id="section-1" ref={ref} className="section-dark min-h-screen flex flex-col justify-center px-6 md:px-16 lg:px-24 py-20 section-animate visible">
      <div className="max-w-6xl mx-auto w-full">
        <div className="text-center mb-10 stagger-1">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-[2px] bg-[#C5A258]" />
            <span className="text-[#C5A258] text-xs font-semibold tracking-[0.2em] uppercase" style={{ fontFamily: 'var(--font-body)' }}>Proposal for New College of Florida {'\u00B7'} Applied Data Science</span>
            <div className="w-12 h-[2px] bg-[#C5A258]" />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-4 tracking-tight leading-[1.05]">Applied Data Science on<br /><span className="text-sky-400">Ukraine's Diia Ecosystem</span></h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed mb-2" style={{ fontFamily: 'var(--font-body)' }}>A student-led research proposal for a practical course on the world's most advanced <CitationLink href="https://www.cgap.org/blog/ukraines-diia-digital-lifeline-in-times-of-crisis">e-government ecosystem</CitationLink></p>
          <p className="text-sm text-[#C5A258] font-semibold" style={{ fontFamily: 'var(--font-body)' }}>Fall 2026 {'\u00B7'} One course {'\u00B7'} One semester {'\u00B7'} The first step toward something bigger</p>
        </div>
        {/* Ukraine / Diia Stats */}
        <div className="stagger-2 mb-4">
          <div className="flex items-center gap-2 mb-3 justify-center">
            <div className="w-8 h-[1px] bg-sky-400/30" />
            <span className="text-sky-400 text-[10px] font-bold tracking-[0.2em] uppercase" style={{ fontFamily: 'var(--font-body)' }}>Ukraine's Diia Ecosystem</span>
            <div className="w-8 h-[1px] bg-sky-400/30" />
          </div>
          <div className="grid grid-cols-3 gap-3 max-w-3xl mx-auto relative">
            {diiaStats.map((s, i) => (
              <button key={i} data-testid={`diia-stat-${i}`} onClick={() => setTooltip(tooltip === `d${i}` ? null : `d${i}`)}
                className="text-center p-5 rounded-xl border bg-sky-950/20 border-sky-500/20 hover:border-sky-400/40 transition-all cursor-pointer">
                <div className={`text-3xl md:text-4xl font-bold ${s.color} mb-1`} style={{ fontFamily: 'var(--font-display)' }}>
                  <AnimatedCounter end={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} active={active} />
                </div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium" style={{ fontFamily: 'var(--font-body)' }}>{s.label}</div>
              </button>
            ))}
            {typeof tooltip === 'string' && tooltip.startsWith('d') && (
              <div className="absolute -bottom-10 left-0 right-0 text-center tooltip-enter">
                <span className="inline-block bg-slate-800 border border-slate-600 text-slate-300 text-xs px-4 py-2 rounded-lg">{diiaTooltips[parseInt(tooltip[1])]}</span>
              </div>
            )}
          </div>
        </div>
        {/* The Gap Bridge */}
        <div className="text-center my-8 stagger-3" data-testid="zero-bridge">
          <div className="inline-block bg-red-950/30 border border-red-800/30 rounded-2xl px-10 py-5">
            <div className="text-5xl md:text-6xl font-bold text-red-400 mb-1" style={{ fontFamily: 'var(--font-display)' }}>0</div>
            <div className="text-[11px] text-red-400/70 uppercase tracking-wider font-medium" style={{ fontFamily: 'var(--font-body)' }}>Universities doing applied data science on this ecosystem</div>
          </div>
        </div>
        {/* NCF Stats */}
        <div className="stagger-4 mb-8">
          <div className="flex items-center gap-2 mb-3 justify-center">
            <div className="w-8 h-[1px] bg-[#C5A258]/30" />
            <span className="text-[#C5A258] text-[10px] font-bold tracking-[0.2em] uppercase" style={{ fontFamily: 'var(--font-body)' }}>New College of Florida</span>
            <div className="w-8 h-[1px] bg-[#C5A258]/30" />
          </div>
          <div className="grid grid-cols-3 gap-3 max-w-3xl mx-auto relative">
            {ncfStats.map((s, i) => (
              <button key={i} data-testid={`ncf-stat-${i}`} onClick={() => setTooltip(tooltip === `n${i}` ? null : `n${i}`)}
                className="text-center p-5 rounded-xl border bg-[#C5A258]/5 border-[#C5A258]/20 hover:border-[#C5A258]/40 transition-all cursor-pointer">
                <div className={`text-3xl md:text-4xl font-bold ${s.color} mb-1`} style={{ fontFamily: 'var(--font-display)' }}>
                  <AnimatedCounter end={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} active={active} />
                </div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium" style={{ fontFamily: 'var(--font-body)' }}>{s.label}</div>
              </button>
            ))}
            {typeof tooltip === 'string' && tooltip.startsWith('n') && (
              <div className="absolute -bottom-10 left-0 right-0 text-center tooltip-enter">
                <span className="inline-block bg-slate-800 border border-slate-600 text-slate-300 text-xs px-4 py-2 rounded-lg">{ncfTooltips[parseInt(tooltip[1])]}</span>
              </div>
            )}
          </div>
        </div>
        {/* Course framing */}
        <div className="text-center max-w-3xl mx-auto stagger-5">
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-8 backdrop-blur-sm">
            <p className="text-lg text-slate-300 leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
              <span className="text-white font-bold">One course. One semester. Fall 2026.</span> 12{'\u2013'}14 students in small groups in a Practical Data Science course analyzing real Ukrainian government data {'\u2014'}
              <span className="text-sky-400 font-semibold"> the same proven model as the USDA partnership</span>.
              Start with a semester, build toward an <span className="text-[#C5A258] font-semibold">FSU-style long-term engagement</span>.
            </p>
          </div>
        </div>
        {/* Ecosystem Panel */}
        <div className="mt-8 text-center stagger-6">
          <button data-testid="toggle-ecosystem" onClick={() => setShowEcosystem(!showEcosystem)}
            className="text-sm text-sky-400 hover:text-sky-300 flex items-center gap-1.5 mx-auto transition" style={{ fontFamily: 'var(--font-body)' }}>
            {showEcosystem ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showEcosystem ? "Hide" : "Explore"} the Diia Ecosystem ({DIIA_ECOSYSTEM.length} components)
          </button>
          {showEcosystem && (
            <div className="grid grid-cols-5 gap-3 mt-5 tooltip-enter">
              {DIIA_ECOSYSTEM.map((comp, i) => {
                const Icon = ecosystemIcons[comp.icon] || Globe;
                return (
                  <div key={i} data-testid={`ecosystem-${i}`} className="bg-slate-800/70 border border-slate-700/50 rounded-xl p-4 text-left">
                    <Icon className="w-5 h-5 text-sky-400 mb-2" />
                    <div className="font-bold text-sm text-white mb-1">{comp.name}</div>
                    <div className="text-[10px] text-sky-400 font-semibold mb-1.5">{comp.stat}</div>
                    <p className="text-[11px] text-slate-400 leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>{comp.desc}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <p className="text-sm text-slate-600 mt-8 text-center" style={{ fontFamily: 'var(--font-body)' }}>Proposed Fall 2026 {'\u00B7'} Prepared by Matthew R. MacFarlane</p>
        <CitationBar citations={NEWS_CITATIONS.hero} dark />
      </div>
    </div>
  );
}
